import { Component, effect, inject, input, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_LOADER } from '@progress/kendo-angular-indicators';
import { KENDO_LAYOUT } from '@progress/kendo-angular-layout';
import { PublicUsersClient, ResendUserConfirmationDto } from '@unity/clients';
import { TrackFormDirective } from '@unity/directives/track-form.directive';
import { TextboxControlComponent } from '@unity/forms/textbox-control/textbox-control.component';
import { OutsideContainerComponent } from '@unity/layouts/outside-container/outside-container.component';
import { emailFormControl } from '@unity/utils/form-controls';
import { FormGroupControlSchema } from '@unity/utils/types';
import { take } from 'rxjs';

class ResendUserConfirmationSchema implements FormGroupControlSchema<ResendUserConfirmationDto> {
  public username = emailFormControl();
}

@Component({
  selector: 'unity-login-resend-confirmation-page',
  standalone: true,
  imports: [
    KENDO_BUTTON,
    KENDO_LOADER,
    KENDO_LAYOUT,
    ReactiveFormsModule,
    TrackFormDirective,
    OutsideContainerComponent,
    RouterLink,
    TextboxControlComponent,
  ],
  templateUrl: './login-resend-confirmation-page.component.html',
  styleUrl: './login-resend-confirmation-page.component.scss',
})
export class LoginResendConfirmationPageComponent {
  private readonly publicUsersClient = inject(PublicUsersClient);
  private readonly formBuilder = inject(FormBuilder);

  protected modalHeaderSubtitle = 'User Confirmation';
  protected modalHeaderTitle = 'Resend';

  protected readonly $isLoading = signal<boolean>(false);
  protected readonly $complete = signal<boolean>(false);

  public readonly $username = input(undefined, { alias: 'username' });

  protected form = this.formBuilder.group(new ResendUserConfirmationSchema());

  public constructor() {
    effect(() => {
      const username = this.$username();
      if (username) {
        this.form.controls.username.setValue(username);
        this.onResetSubmit();
      }
    });
  }

  protected onResetSubmit() {
    this.$complete.set(false);
    this.$isLoading.set(true);
    this.publicUsersClient
      .createOneConfirmationEmail({
        username: this.form.controls.username.value,
      })
      .pipe(take(1))
      .subscribe({
        error: () => {
          this.$isLoading.set(false);
        },
        complete: () => {
          this.$isLoading.set(false);
          this.$complete.set(true);
        },
      });
  }
}
